/* BD HERBS — video carousel with a reel-style popup player.
   - Card videos have no src until they come into view (poster only before that).
   - Popup videos have no src or poster until the popup opens (a poster attribute downloads even inside a closed dialog).
   - Only cards on screen play (muted); the rest stay paused.
   - Reduced motion / data saver: no autoplay, posters only.
   - Tapping a card opens the popup with sound; swipe sideways for the next video.
     Only the active popup video plays, so only one video has sound at a time. */
if (!customElements.get('bd-video-carousel')) {
  customElements.define(
    'bd-video-carousel',
    class BdVideoCarousel extends HTMLElement {
      connectedCallback() {
        // Reconnected (theme editor moving sections): observers were disconnected, listeners are still there
        if (this.bound) {
          this.cardVideos.forEach((video) => this.cardObserver?.observe(video));
          this.slides?.forEach((slide) => this.slideObserver?.observe(slide));
          return;
        }
        this.bound = true;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const saveData = navigator.connection && navigator.connection.saveData === true;
        this.autoplay = !reduceMotion && !saveData;
        this.cardVideos = [...this.querySelectorAll('[data-vc-card-video]')];
        this.visible = new Set();
        this.popupOpen = false;
        this.muted = false;

        if (this.autoplay && this.cardVideos.length) this.watchCards();

        this.querySelectorAll('[data-vc-open]').forEach((button) => {
          button.addEventListener('click', () => this.open(Number(button.dataset.vcOpen)));
        });

        this.dialog = this.querySelector('[data-vc-dialog]');
        if (this.dialog) this.setupDialog();
      }

      disconnectedCallback() {
        this.cardObserver?.disconnect();
        this.slideObserver?.disconnect();
      }

      load(video, preload = 'auto') {
        if (!video.src && video.dataset.src) {
          if (video.dataset.poster) video.poster = video.dataset.poster;
          video.src = video.dataset.src;
          video.preload = preload;
        }
      }

      play(video) {
        this.load(video);
        const attempt = video.play();
        if (attempt) attempt.catch(() => {});
        return attempt;
      }

      /* ---------- Cards ---------- */
      watchCards() {
        this.cardVideos.forEach((video) => {
          video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
        });

        this.cardObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                this.visible.add(video);
                if (!this.popupOpen) this.play(video);
              } else {
                this.visible.delete(video);
                video.pause();
              }
            });
          },
          { threshold: 0.4 }
        );
        this.cardVideos.forEach((video) => this.cardObserver.observe(video));
      }

      pauseCards() {
        this.cardVideos.forEach((video) => video.pause());
      }

      resumeCards() {
        if (!this.autoplay) return;
        this.visible.forEach((video) => this.play(video));
      }

      /* ---------- Popup ---------- */
      setupDialog() {
        this.track = this.dialog.querySelector('[data-vc-track]');
        this.slides = [...this.dialog.querySelectorAll('[data-vc-slide]')];
        this.slideVideos = this.slides.map((slide) => slide.querySelector('video'));
        this.playButton = this.dialog.querySelector('[data-vc-play]');
        this.muteButton = this.dialog.querySelector('[data-vc-mute]');
        this.active = 0;

        this.slideVideos.forEach((video) => {
          video.addEventListener('play', () => this.syncButtons());
          video.addEventListener('pause', () => this.syncButtons());
          video.addEventListener('click', () => this.togglePlay());
        });

        this.slideObserver = new IntersectionObserver(
          (entries) => {
            if (!this.popupOpen) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.activate(Number(entry.target.dataset.vcSlide));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.slideObserver.observe(slide));

        this.playButton?.addEventListener('click', () => this.togglePlay());
        this.muteButton?.addEventListener('click', () => this.toggleMute());
        this.dialog.querySelector('[data-vc-close]')?.addEventListener('click', () => this.dialog.close());
        this.dialog.querySelector('[data-vc-prev]')?.addEventListener('click', () => this.go(-1));
        this.dialog.querySelector('[data-vc-next]')?.addEventListener('click', () => this.go(1));

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(1);
          if (event.key === 'ArrowLeft') this.go(-1);
        });

        this.dialog.addEventListener('close', () => this.onClose());

        /* Quick add from inside the popup: pause; single-variant products open the cart drawer, so step aside */
        this.dialog.addEventListener('click', (event) => {
          const button = event.target.closest('[data-bd-quick-add]');
          if (!button) return;
          this.slideVideos[this.active]?.pause();
          if (button.dataset.single === 'true') this.dialog.close();
        });
      }

      open(index) {
        if (!this.dialog) return;
        this.popupOpen = true;
        this.pauseCards();
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        const slide = this.slides[index];
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        this.activate(index, true);
      }

      onClose() {
        this.popupOpen = false;
        this.slideVideos.forEach((video) => video.pause());
        document.body.classList.remove('overflow-hidden');
        this.resumeCards();
      }

      activate(index, force = false) {
        if (!force && index === this.active && !this.slideVideos[index].paused) return;
        this.active = index;
        this.slideVideos.forEach((video, i) => {
          if (i !== index) {
            video.pause();
            return;
          }
          video.muted = this.muted;
          const attempt = this.play(video);
          /* Some phones refuse sound without a fresh tap: fall back to muted playback */
          if (attempt && !video.muted) {
            attempt.catch(() => {
              this.muted = true;
              video.muted = true;
              this.play(video);
              this.syncButtons();
            });
          }
        });
        /* Warm up the neighbours so swiping feels instant */
        [index - 1, index + 1].forEach((i) => {
          const video = this.slideVideos[i];
          if (video) this.load(video, 'metadata');
        });
        this.syncButtons();
      }

      go(direction) {
        const next = Math.min(this.slides.length - 1, Math.max(0, this.active + direction));
        if (next === this.active) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      togglePlay() {
        const video = this.slideVideos[this.active];
        if (!video) return;
        if (video.paused) this.play(video);
        else video.pause();
      }

      toggleMute() {
        this.muted = !this.muted;
        const video = this.slideVideos[this.active];
        if (video) {
          video.muted = this.muted;
          if (!this.muted && video.paused) this.play(video);
        }
        this.syncButtons();
      }

      syncButtons() {
        const video = this.slideVideos?.[this.active];
        if (!video) return;
        const paused = video.paused;
        this.dialog.toggleAttribute('data-paused', paused);
        this.dialog.toggleAttribute('data-muted', this.muted);
        if (this.playButton) {
          this.playButton.setAttribute('aria-label', paused ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause);
        }
        this.muteButton?.setAttribute('aria-pressed', String(this.muted));
        const prev = this.dialog.querySelector('[data-vc-prev]');
        const next = this.dialog.querySelector('[data-vc-next]');
        if (prev) prev.disabled = this.active === 0;
        if (next) next.disabled = this.active === this.slides.length - 1;
      }
    }
  );
}
